import { detailedDiff } from 'deep-object-diff';
import {
  cloneDeep,
  isArray,
  isEqual,
  isNil,
  isObjectLike,
  keys,
  merge,
  union,
} from 'lodash';

/**
 * This function takes in two objects and returns the keys that don't have matching values.
 *
 * @param objA First object to compare.
 * @param objB Second object to compare.
 * @param path Primarily used by the function to track the path while recurrsing. However, it's
 * possible to pass in a path to compare a specific branch.
 * @returns An array of paths to mismatching keys.
 */
export function getDifferentKeyPaths(
  objA: Record<string, unknown>,
  objB: Record<string, unknown>,
  path = ''
): string[] {
  if (!isObject(objA) || !isObject(objB)) {
    return isEqual(objA, objB) ? [] : [path];
  }

  const formKeys = union(keys(objA), keys(objB));

  return formKeys.reduce((result: string[], key: string) => {
    const newPath = path ? `${path}.${key}` : key;
    const differences = getDifferentKeyPaths(
      // asserting to any since unknown is not assignable and compiler is happy
      objA[key] as any,
      objB[key] as any,
      newPath
    );
    return result.concat(differences);
  }, []);
}

/**
 * This function takes in an object and recursively removes the specified keys
 * from it.
 *
 * @param obj The object to remove keys from.
 * @param keys The keys to remove from the object.
 * @returns A copy of the object with the specified keys removed.
 */
export const removeProperties = (obj: any, keys: string[]): any => {
  const isPrimitive = ['boolean', 'string', 'number', 'undefined'].includes(
    typeof obj
  );

  if (isPrimitive || isNil(obj) || obj instanceof Date) {
    return obj;
  }

  const copy = cloneDeep(obj);

  for (const [k, v] of Object.entries(copy)) {
    if (keys.includes(k)) {
      delete copy[k];
    } else {
      if (Array.isArray(v)) {
        copy[k] = v.map((val) => {
          return removeProperties(val, keys);
        });
      } else {
        copy[k] = removeProperties(v, keys);
      }
    }
  }

  return copy;
};

interface IDetailedDiff<T> {
  added: T;
  updated: T;
  deleted: T;
}

export const diff = <T extends object>(previous: T, current: T): Partial<T> => {
  const { added, updated, deleted } = detailedDiff(
    previous,
    current
  ) as IDetailedDiff<T>;
  const merged: T = {} as T;

  merge(merged, added);
  merge(merged, updated);

  (Object.keys(deleted) as (keyof T)[]).forEach((key) => {
    if (Array.isArray(current[key])) {
      deleted[key] = current[key];
    }
  });

  merge(merged, deleted);

  return merged;
};

export const replaceVal = <T>(src: T, val: unknown, replace: unknown): T => {
  if (isArray(src)) {
    return src.map((v) => replaceVal(v, val, replace)) as unknown as T;
  } else if (isObject(src)) {
    return (Object.keys(src) as (keyof T)[]).reduce((prev, curr) => {
      return {
        ...prev,
        [curr]: replaceVal(src[curr], val, replace),
      };
    }, {} as T);
  } else if (src === val) {
    return replace as T;
  } else {
    return src;
  }
};

export const isObject = (value: unknown): value is Record<string, unknown> => {
  const isDate = value instanceof Date;
  return isObjectLike(value) && !Array.isArray(value) && !isDate;
};
